import _ from 'lodash';

import React, { useContext, useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Stepper, Step, StepLabel, StepConnector, Box, IconButton } from '@material-ui/core';
import PropTypes from 'prop-types';
import { ShootingContext } from 'contexts';
import { Shooting } from 'models';
import { SHOOTING_STEPS } from 'config/constants';
import ChevronLeftRoundedIcon from '@material-ui/icons/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@material-ui/icons/ChevronRightRounded';
import LocationStep from './LocationStep';
import PropertyStep from './PropertyStep';
import PackageStep from './PackageStep';
import ServiceStep from './ServiceStep';
import ClientInfoStep from './ClientInfoStep';
import ReviewStep from './ReviewStep';
import SelectDateTimeStep from './SelectDateTimeStep';

import useStyles from './styles';

interface Props {
  isUpdateDate: boolean;
  updateTitle: (_: string) => void;
  onClose: () => void;
}

function ShootingWizard({ isUpdateDate, updateTitle, onClose }: Props) {
  const intl = useIntl();
  const styles = useStyles();

  const {
    listing,
    addressFound,
    isServicable,
    validateLocationStep,
    validatePropertyStep,
    validatePackageStep,
    validateClientInfoStep,
  } = useContext(ShootingContext);

  const initialStep = isUpdateDate ? SHOOTING_STEPS.SELECT_DATETIME : 0;
  const [activeStep, setActiveStep] = useState(initialStep);

  useEffect(() => {
    const title = !activeStep
      ? intl.formatMessage({ id: 'shooting.create.title' })
      : listing.fullAddress;

    updateTitle(title);
  }, [activeStep]);

  const steps = [
    {
      label: intl.formatMessage({ id: 'shooting.create.steps.location' }),
      validator: validateLocationStep,
    },
    {
      label: intl.formatMessage({ id: 'shooting.create.steps.property' }),
      validator: validatePropertyStep,
    },
    {
      label: intl.formatMessage({ id: 'shooting.create.steps.package' }),
      validator: () => {
        return true;
      },
    },
    {
      label: intl.formatMessage({ id: 'shooting.create.steps.service' }),
      validator: validatePackageStep,
    },
    {
      label: intl.formatMessage({ id: 'shooting.create.steps.optional' }),
      validator: validateClientInfoStep,
    },
    {
      label: intl.formatMessage({ id: 'shooting.create.steps.summary' }),
      validator: () => {
        return true;
      },
    },
  ];

  const stepContent = () => {
    switch (activeStep) {
      case SHOOTING_STEPS.LOCATION:
        return <LocationStep setActiveStep={setActiveStep} onCancel={onClose} />;
      case SHOOTING_STEPS.PROPERTY:
        return <PropertyStep setActiveStep={setActiveStep} onCancel={onClose} />;
      case SHOOTING_STEPS.PACKAGE:
        return <PackageStep setActiveStep={setActiveStep} onCancel={onClose} />;
      case SHOOTING_STEPS.SERVICE:
        return <ServiceStep setActiveStep={setActiveStep} onCancel={onClose} />;
      case SHOOTING_STEPS.CLIENT_INFO:
        return <ClientInfoStep setActiveStep={setActiveStep} onCancel={onClose} />;
      case SHOOTING_STEPS.REVIEW:
        return <ReviewStep setActiveStep={setActiveStep} onCancel={onClose} />;
      case SHOOTING_STEPS.SELECT_DATETIME:
        return (
          <SelectDateTimeStep
            isUpdateDate={isUpdateDate}
            setActiveStep={setActiveStep}
            onCancel={onClose}
          />
        );
      default:
        return <LocationStep setActiveStep={setActiveStep} onCancel={onClose} />;
    }
  };

  const onStepClick = (index: number) => {
    return () => {
      let isValid = true;
      for (let i = 0; i < index; i++) {
        if (!steps[i].validator()) {
          isValid = false;
          break;
        }
      }

      if (isValid) {
        setActiveStep(index);
      }
    };
  };

  const onPreviousStep = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    } else {
      onClose();
    }
  };

  const onNextStep = () => {
    if (activeStep < steps.length - 1) {
      let isValid = true;
      for (let i = 0; i < activeStep; i++) {
        if (!steps[i + 1].validator()) {
          isValid = false;
          break;
        }
      }

      if (isValid) {
        setActiveStep(activeStep + 1);
      }
    } else {
      setActiveStep(SHOOTING_STEPS.SELECT_DATETIME);
    }
  };

  return (
    <Box className={styles.shootingWizard}>
      <div className={styles.row}>
        {addressFound && activeStep < steps.length && (
          <IconButton className={styles.backButton} color='secondary' onClick={onPreviousStep}>
            <ChevronLeftRoundedIcon />
          </IconButton>
        )}
        {addressFound && activeStep < steps.length ? (
          <Stepper className={styles.stepper} activeStep={activeStep} connector={null}>
            {steps.map((m, index: number) => {
              return (
                <Step key={m.label} className={styles.step}>
                  <StepLabel
                    classes={{
                      root: styles.stepLabel,
                      label: styles.stepLabelText,
                      iconContainer: styles.stepIconContainer,
                    }}
                    onClick={onStepClick(index)}
                  >
                    {m.label}
                  </StepLabel>
                </Step>
              );
            })}
            <StepConnector className={styles.stepConnector} />
          </Stepper>
        ) : null}
        {addressFound && isServicable && activeStep < steps.length && (
          <IconButton className={styles.nextButton} color='secondary' onClick={onNextStep}>
            <ChevronRightRoundedIcon />
          </IconButton>
        )}
      </div>
      <Box className={styles.stepContent}>{stepContent()}</Box>
    </Box>
  );
}

ShootingWizard.defaultProps = {
  isUpdateDate: false,
  onClose: _.noop,
};

ShootingWizard.propTypes = {
  isUpdateDate: PropTypes.bool,
  onClose: PropTypes.func,
};

export default ShootingWizard;
